<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  Activity,
  Building2,
  Circle,
  Clapperboard,
  House,
  LogIn,
  LogOut,
  Moon,
  Settings,
  Sun,
} from 'lucide-vue-next'
import { useClient, useStore } from '@codecures/live-stream-vue'
import UiAvatar from '@/components/ui/UiAvatar.vue'
import AppMark from './AppMark.vue'
import ThemeButton from './ThemeButton.vue'
import { useTheme } from '@/lib/theme'

/**
 * Every page but the meeting itself sits in this: a rail of icons down the
 * left on a wide screen (the light and dark switch at its foot, as in the
 * notes dashboard), a bar across the top and tabs along the bottom on a
 * phone. The page goes in the slot.
 */
const client = useClient()
const auth = useStore(client.auth.state)
const route = useRoute()
const { dark, set } = useTheme()

/** Admins get the admin page (the editor's AI model) in the rail. */
const admin = ref(false)
watch(
  () => auth.value.user?.id,
  async (id) => {
    admin.value = false
    if (!id) return
    try {
      admin.value = await client.ai.isAdmin()
    } catch {
      // Not shown.
    }
  },
  { immediate: true },
)

const links = computed(() => [
  {
    to: '/',
    label: 'Meetings',
    icon: House,
    match: (p: string) => p === '/' || p.startsWith('/room'),
  },
  ...(auth.value.user
    ? [
        {
          to: '/videos',
          label: 'Videos',
          icon: Clapperboard,
          match: (p: string) => p.startsWith('/videos') || p.startsWith('/v/'),
        },
        {
          to: '/record',
          label: 'Record',
          icon: Circle,
          match: (p: string) => p.startsWith('/record'),
        },
        {
          to: '/org',
          label: 'Organisation',
          short: 'Org',
          icon: Building2,
          match: (p: string) => p.startsWith('/org') || p.startsWith('/join/org'),
        },
      ]
    : []),
  {
    to: '/system',
    label: 'System check',
    short: 'System',
    icon: Activity,
    match: (p: string) => p.startsWith('/system'),
  },
  ...(admin.value
    ? [{ to: '/admin', label: 'Admin', icon: Settings, match: (p: string) => p === '/admin' }]
    : []),
])
const active = (l: { match: (p: string) => boolean }) => l.match(route.path)
</script>

<template>
  <div class="flex min-h-full flex-col md:flex-row">
    <!-- The rail, on a wide screen. -->
    <nav
      aria-label="Main"
      class="card sticky top-4 z-30 m-4 hidden h-[calc(100vh-2rem)] w-18 shrink-0 flex-col items-center gap-2 py-4 md:flex"
    >
      <RouterLink to="/" class="mb-3" aria-label="live-stream home"><AppMark /></RouterLink>
      <RouterLink
        v-for="l in links"
        :key="l.to"
        :to="l.to"
        :title="l.label"
        :aria-label="l.label"
        :aria-current="active(l) ? 'page' : undefined"
        class="flex size-11 items-center justify-center rounded-2xl transition-colors"
        :class="
          active(l)
            ? 'bg-surface-raised text-primary shadow-card'
            : 'text-muted hover:bg-surface-muted hover:text-fg'
        "
      >
        <component :is="l.icon" class="size-5" :stroke-width="1.75" />
      </RouterLink>

      <div class="mt-auto flex flex-col items-center gap-1 rounded-full bg-surface-muted p-1">
        <button
          type="button"
          aria-label="Light"
          title="Light"
          :aria-pressed="!dark"
          class="flex size-8 items-center justify-center rounded-full transition-colors"
          :class="!dark ? 'bg-surface-raised text-fg shadow-card' : 'text-faint hover:text-fg'"
          @click="set('light')"
        >
          <Sun class="size-4" />
        </button>
        <button
          type="button"
          aria-label="Dark"
          title="Dark"
          :aria-pressed="dark"
          class="flex size-8 items-center justify-center rounded-full transition-colors"
          :class="dark ? 'bg-surface-raised text-fg shadow-card' : 'text-faint hover:text-fg'"
          @click="set('dark')"
        >
          <Moon class="size-4" />
        </button>
      </div>

      <template v-if="auth.user">
        <UiAvatar :id="auth.user.id" :name="auth.user.displayName" size="sm" class="mt-2" />
        <button
          type="button"
          title="Sign out"
          aria-label="Sign out"
          class="flex size-9 items-center justify-center rounded-full text-faint hover:bg-surface-muted hover:text-fg"
          @click="client.auth.logout()"
        >
          <LogOut class="size-4" />
        </button>
      </template>
      <RouterLink
        v-else
        to="/signin"
        title="Sign in"
        aria-label="Sign in"
        class="mt-2 flex size-10 items-center justify-center rounded-full text-muted hover:bg-surface-muted hover:text-fg"
      >
        <LogIn class="size-5" />
      </RouterLink>
    </nav>

    <!-- The bar, on a phone. -->
    <header
      class="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-surface px-4 py-3 backdrop-blur-xl md:hidden"
    >
      <RouterLink to="/" aria-label="live-stream home"><AppMark size="sm" /></RouterLink>
      <span class="font-semibold tracking-tight">live-stream</span>
      <ThemeButton class="ml-auto" />
      <UiAvatar v-if="auth.user" :id="auth.user.id" :name="auth.user.displayName" size="sm" />
      <RouterLink v-else to="/signin" class="text-sm font-medium text-primary">Sign in</RouterLink>
    </header>

    <div class="min-w-0 flex-1 pb-20 md:pb-0">
      <slot />
    </div>

    <!-- The tabs, on a phone. -->
    <nav
      aria-label="Main"
      class="fixed inset-x-0 bottom-0 z-30 flex justify-around border-t border-line bg-surface px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] backdrop-blur-xl md:hidden"
    >
      <RouterLink
        v-for="l in links"
        :key="l.to"
        :to="l.to"
        :aria-current="active(l) ? 'page' : undefined"
        class="flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl px-1 py-1 text-[11px]"
        :class="active(l) ? 'text-primary' : 'text-muted'"
      >
        <component :is="l.icon" class="size-5" :stroke-width="1.75" />
        {{ 'short' in l ? l.short : l.label }}
      </RouterLink>
      <button
        v-if="auth.user"
        type="button"
        class="flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl px-1 py-1 text-[11px] text-muted"
        @click="client.auth.logout()"
      >
        <LogOut class="size-5" :stroke-width="1.75" />
        Sign out
      </button>
    </nav>
  </div>
</template>
